import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, Link2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { formatAmount } from "../../lib/finance";
import { linkedMessage, procurementError, unlinkedSummary } from "../../lib/procurement";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Skeleton } from "../ui/skeleton";

const detail = (item) => [`${item.expenses} dépense${item.expenses > 1 ? "s" : ""}`, item.subscriptions ? `${item.subscriptions} abonnement${item.subscriptions > 1 ? "s" : ""}` : null,
  item.totals.map((t) => formatAmount(t.amount, t.currency)).join(" · ") || null].filter(Boolean).join(" · ");

function ReconcileRow({ item, suppliers, busy, onCreate, onLink }) {
  const [chosen, setChosen] = useState("");
  const tied = item.match?.active ? item.match : null;
  return (
    <li className="py-3 space-y-2 md:space-y-0 md:flex md:items-center md:justify-between md:gap-3" data-testid="reconcile-row">
      <div className="min-w-0">
        <div className="font-medium text-slate-900 truncate">{item.name}</div>
        <div className="text-xs text-slate-500">{detail(item)}</div>
      </div>
      <div className="flex flex-wrap items-center gap-2 shrink-0">
        {tied ? (
          <Button size="sm" disabled={busy} onClick={() => onLink(item, tied.id)} data-testid="reconcile-match">
            {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : <Link2 size={14} className="mr-1.5" />}Rattacher à « {tied.name} »
          </Button>
        ) : (
          <>
            <Button size="sm" disabled={busy} onClick={() => onCreate(item)} data-testid="reconcile-create">
              {busy ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}Créer la fiche
            </Button>
            {suppliers.length > 0 && (
              <Select value={chosen} onValueChange={(id) => { setChosen(id); onLink(item, id); }} disabled={busy}>
                <SelectTrigger className="h-9 w-44" aria-label={`Rattacher ${item.name} à une fiche existante`} data-testid="reconcile-pick"><SelectValue placeholder="Rattacher à…" /></SelectTrigger>
                <SelectContent>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            )}
          </>
        )}
      </div>
    </li>
  );
}

function ReconcileDialog({ items, onClose, onDone }) {
  const [suppliers, setSuppliers] = useState([]);
  const [busy, setBusy] = useState("");
  useEffect(() => {
    api.get("/procurement/suppliers", { params: { limit: 200, active: "active" } }).then((r) => setSuppliers(r.data.items || [])).catch(() => setSuppliers([]));
  }, []);

  const link = async (item, supplierId) => {
    setBusy(item.name);
    try {
      const { data } = await api.post("/finance/suppliers/link", { name: item.name, supplier_id: supplierId });
      toast.success(linkedMessage(data));
      onDone();
    } catch (e) {
      toast.error(procurementError(e, "Impossible de rattacher ce fournisseur."));
    } finally {
      setBusy("");
    }
  };
  // The sheet is created with the name as written in Finance (the title the person already knows), then every expense carrying it is tied.
  const create = async (item) => {
    setBusy(item.name);
    try {
      const { data: sheet } = await api.post("/procurement/suppliers", { name: item.name, category: "other" });
      const { data } = await api.post("/finance/suppliers/link", { name: item.name, supplier_id: sheet.id });
      toast.success(`Fiche créée. ${linkedMessage(data)}`);
      onDone();
    } catch (e) {
      toast.error(procurementError(e, "Impossible de créer la fiche fournisseur."));
    } finally {
      setBusy("");
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-2xl" data-testid="reconcile-dialog">
        <DialogHeader>
          <DialogTitle>Rapprocher les fournisseurs des dépenses</DialogTitle>
          <DialogDescription>Ces noms sont écrits dans les dépenses sans fiche fournisseur. Rien n'est créé sans votre clic ; les montants et les noms d'origine ne changent pas.</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? <p className="py-4 text-sm text-slate-600" data-testid="reconcile-done">Tout est rattaché.</p> : (
          <ul className="divide-y divide-slate-100 max-h-[60vh] overflow-y-auto">
            {items.map((item) => <ReconcileRow key={item.name} item={item} suppliers={suppliers} busy={busy === item.name} onCreate={create} onLink={link} />)}
          </ul>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** Banner of the suppliers page: names written on expenses that no supplier sheet is tied to. Renders nothing when there is none. */
export function UnlinkedSuppliers({ onLinked }) {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const load = useCallback(() => {
    api.get("/finance/suppliers/unlinked").then((r) => setItems(r.data.items || [])).catch(() => setItems([]));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (items === null || (items.length === 0 && !open)) return null;
  return (
    <>
      {items.length > 0 && (
        <Card className="flex flex-wrap items-center justify-between gap-3 border-amber-200 bg-amber-50 p-3" data-testid="unlinked-banner">
          <div className="flex items-start gap-2 min-w-0">
            <AlertTriangle size={16} className="mt-0.5 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="text-sm text-slate-800">{unlinkedSummary(items)}.</p>
          </div>
          <Button size="sm" variant="outline" onClick={() => setOpen(true)} data-testid="unlinked-open">Rapprocher</Button>
        </Card>
      )}
      {open && <ReconcileDialog items={items} onClose={() => setOpen(false)} onDone={() => { load(); onLinked?.(); }} />}
    </>
  );
}

/** What a supplier has cost, per currency, and its latest expenses (for whoever may see every expense). */
export function SupplierSpendCard({ supplierId }) {
  const [spend, setSpend] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setSpend(null);
    setFailed(false);
    api.get(`/finance/suppliers/${supplierId}/spend`).then((r) => setSpend(r.data)).catch(() => setFailed(true));
  }, [supplierId]);
  if (failed) return null;
  return (
    <div data-testid="supplier-spend">
      <h3 className="text-sm font-semibold text-slate-900 mb-2">Dépenses liées</h3>
      {!spend ? <Skeleton className="h-16 w-full" /> : spend.totals.length === 0 ? <p className="text-sm text-slate-500">Aucune dépense n'est rattachée à ce fournisseur.</p> : (
        <div className="space-y-2">
          <ul className="space-y-0.5 text-sm text-slate-900" data-testid="supplier-spend-totals">
            {spend.totals.map((t) => <li key={t.currency}><span className="font-semibold">{formatAmount(t.amount, t.currency)}</span> <span className="text-slate-500">· {t.count} dépense{t.count > 1 ? "s" : ""}</span></li>)}
          </ul>
          <ul className="divide-y divide-slate-100" data-testid="supplier-spend-recent">
            {spend.recent.map((e) => (
              <li key={e.id} className="flex items-baseline justify-between gap-3 py-1.5 text-sm">
                <Link to="/finance" state={{ openExpense: e.id }} className="min-w-0 truncate text-jamora hover:underline">{e.title}</Link>
                <span className="shrink-0 text-slate-600">{formatAmount(e.amount, e.currency)}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
